import { Pagination } from '@/domain/models'
import { Category } from '@/domain/models/content/category'
import { ContentFormat } from '@/domain/models/content/content-format'
import { ContentSearchOrder } from '@/domain/models/content/content-search-order'
import { Subtype } from '@/domain/models/content/subtype'
import { Plan } from '@/domain/models/plan-license/plan-model'
import { Account } from '@/domain/usecases'
import { UserContentSearch } from '@/domain/usecases/content/user-content-search'
import { parseNumber } from '@/main/helpers/number-helper'
import { getAvailablePlans } from '@/presentation/helpers/filter-plan-helper'
import { capitalize } from '@mui/material'
import { LDClient } from 'launchdarkly-react-client-sdk'
import { QueryClient } from 'react-query'
import { LoaderFunctionArgs } from 'react-router-dom'
import RegisteredPlanBaseLoader from '../registered-plan-base-loader'

export default class SearchLoader extends RegisteredPlanBaseLoader {
  constructor(
    readonly account: Account.Model,
    readonly queryClient: QueryClient,
    readonly ldClient: LDClient | undefined,
    protected readonly showFullCatalog: boolean = false
  ) {
    super(account, queryClient, ldClient)
  }

  async loader({ request }: LoaderFunctionArgs) {
    const url = new URL(request.url)
    const params = this.createSearchParams(url.searchParams)

    return this.defer({
      data: this.getLoaderPromise(params)
    })
  }

  protected async getLoaderPromise(params: UserContentSearch.Params) {
    return super
      .importRemoteModule('makeRemoteUserContentSearch')
      .then(async makeRemoteUserContentSearch => {
        const remote = makeRemoteUserContentSearch(this.queryClient) as UserContentSearch
        return Promise.all([remote.loader(params), remote])
      })
      .then(([data, remote]) => ({ data, remote, params }))
  }

  protected createSearchParams(searchParams: URLSearchParams): UserContentSearch.Params {
    const queryTerm = searchParams.get(UserContentSearch.QUERY_TERM_PARAM_NAME)
    const plan = searchParams.get(UserContentSearch.PLAN_PARAM_NAME)
    const categories = searchParams.get(UserContentSearch.CATEGORIES_PARAM_NAME)
    const formats = searchParams.get(UserContentSearch.FORMATS_PARAM_NAME)
    const institutions = searchParams.get(UserContentSearch.INSTITUTIONS_PARAM_NAME)
    const languages = searchParams.get(UserContentSearch.LANGUAGES_PARAM_NAME)
    const captions = searchParams.get(UserContentSearch.CAPTIONS_PARAM_NAME)
    const subtypes =
      searchParams.get(UserContentSearch.SUBTYPES_PARAM_NAME) ?? Subtype.Enum.SUBSCRIPTION.name
    const order = searchParams.get(UserContentSearch.ORDER_PARAM_NAME)
    const page = searchParams.get(Pagination.PAGE_PARAM_NAME)
    const pageSize = searchParams.get(Pagination.PAGE_SIZE_PARAM_NAME)

    return {
      queryTerm: queryTerm ? capitalize(queryTerm) : undefined,
      plan: this.parsePlan(plan),
      categories: this.parseCategories(categories),
      formats: this.parseFormats(formats),
      institutions: this.parseInstitutions(institutions),
      languages: this.parseLanguagesFormat(languages),
      captions: this.parseLanguagesFormat(captions),
      subtypes: this.parseSubtypes(subtypes),
      order: this.parseOrder(order),
      page: parseNumber(page, Pagination.DEFAULT_PAGE)!,
      pageSize: parseNumber(pageSize, UserContentSearch.DEFAULT_PAGE_SIZE)
    }
  }

  protected parsePlan(plan: string | null) {
    const availablePlans = getAvailablePlans(this.showFullCatalog, this.account)
    return Plan.getByName(plan) || availablePlans[0]
  }

  protected parseCategories(categories: string | null) {
    if (categories && categories.length) {
      return categories.split(',').map(Category.getByName)
    } else {
      return []
    }
  }

  protected parseFormats(formats: string | null) {
    if (formats && formats.length) {
      return formats.split(',').map(ContentFormat.getByName)
    } else {
      return []
    }
  }

  protected parseInstitutions(institutions: string | null) {
    if (institutions && institutions.length) {
      return institutions.split(',').map(Number)
    } else {
      return []
    }
  }

  protected parseLanguagesFormat(languagesFormat: string | null) {
    if (languagesFormat && languagesFormat.length) {
      return languagesFormat.split(',')
    } else {
      return []
    }
  }

  protected parseSubtypes(subtypes: string | null) {
    if (!subtypes || subtypes === 'none') {
      return []
    } else {
      return subtypes.split(',').map(Subtype.getByName)
    }
  }

  protected parseOrder(order: string | null) {
    if (order && order.length) {
      const parsed = ContentSearchOrder.getByName(order)
      return parsed ? parsed : ContentSearchOrder.Enum.RECOMMENDED
    }
    return ContentSearchOrder.Enum.RECOMMENDED
  }
}
